/**
 * Display-only helpers describing how far a talent user is through the
 * application / profile, and roughly how long the rest will take.
 *
 * These read the existing step methods (NeedsCompletion, hasCompleted) and
 * never change validation, saving or submission behaviour.
 */

/**
 * Estimated minutes to complete each section, keyed by step path.
 * Tweak these numbers here; anything missing falls back to the default.
 * Application totals match the "about 12 min" promised on signup, and the
 * remaining profile sections the "about 11 min".
 */
export const STEP_MINUTES = {
    // Application
    '/basic-info': 2,
    '/bio': 2,
    '/references': 1,
    '/roles-and-skills': 2,
    '/ai-expertise': 1,
    '/project-interests': 1,
    '/recommended-skills': 1,
    '/industry-experience': 1,
    '/work-preferences': 1,
    '/video-interview': 10,

    // Profile only
    '/work-status': 1,
    '/brands': 2,
    '/project-case-studies': 4,
    '/work-history': 4,
    '/education': 1,
    '/certification': 1,
    '/awards': 1,
};

export const DEFAULT_STEP_MINUTES = 1;

export const getStepMinutes = (step) => {
    if (!step) return 0;
    if (typeof step.StepMinutes === 'number') return step.StepMinutes;
    return STEP_MINUTES[step.path] ?? DEFAULT_STEP_MINUTES;
};

/**
 * Sections that can be "complete" from defaults alone (e.g. Worker
 * Classification is pre-set to W2 for new accounts). For display purposes we
 * only show these as done once the user has saved the section themselves.
 * This never blocks validation or submission.
 */
export const CONFIRM_REQUIRED_PATHS = ['/work-preferences'];

const confirmKey = (user, path) => `talentApp.confirmed.${user?.id}.${path}`;

export const isSectionConfirmed = (user, step) => {
    if (!step || !CONFIRM_REQUIRED_PATHS.includes(step.path)) return true;
    if (user?.didSubmitApplication?.() || user?.isApplicationApproved?.()) return true;
    try {
        return !!window.localStorage.getItem(confirmKey(user, step.path));
    } catch (e) {
        return false;
    }
};

export const markSectionConfirmed = (user, step) => {
    if (!user?.id || !step || !CONFIRM_REQUIRED_PATHS.includes(step.path)) return;
    try {
        window.localStorage.setItem(confirmKey(user, step.path), new Date().toISOString());
    } catch (e) {
        // storage unavailable; display falls back to "Confirm"
    }
};

const safely = (fn, fallback = false) => {
    try {
        return fn();
    } catch (e) {
        console.warn('applicationProgress', e);
        return fallback;
    }
};

/**
 * Summarise progress for the given steps (defaults to the user's current
 * application/profile steps). Only steps shown in the sidebar (those with a
 * StepTitle and StepInfo) are counted.
 */
export const getJourneyProgress = (user, steps = null) => {
    const allSteps = steps || safely(() => user?.currentProfileSteps?.(), []) || [];

    const sections = allSteps
        .filter(step => step?.StepTitle && step?.StepInfo)
        .map(step => {
            const completed = !!safely(() => step.hasCompleted?.(user));
            const needsConfirm = completed && !isSectionConfirmed(user, step);
            return {
                step,
                title: step.StepTitle,
                path: step.path,
                section: step.StepSection || null,
                required: !!safely(() => step.NeedsCompletion?.(user)),
                completed: completed && !needsConfirm,
                needsConfirm,
                minutes: getStepMinutes(step),
            };
        });

    const required = sections.filter(s => s.required);
    const optional = sections.filter(s => !s.required);
    const requiredDone = required.filter(s => s.completed);
    const requiredRemaining = required.filter(s => !s.completed);

    const totalMinutes = required.reduce((sum, s) => sum + s.minutes, 0);
    const minutesLeft = requiredRemaining.reduce((sum, s) => sum + s.minutes, 0);

    return {
        sections,
        required,
        optional,
        requiredDone,
        requiredRemaining,
        requiredCount: required.length,
        requiredDoneCount: requiredDone.length,
        percent: required.length ? Math.round((requiredDone.length / required.length) * 100) : 100,
        totalMinutes,
        minutesLeft,
        nextSection: requiredRemaining[0] || null,
    };
};

export const formatMinutes = (minutes) => {
    if (!minutes || minutes <= 0) return 'under a minute';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

/**
 * Optional profile sections worth suggesting once the required ones are done
 * (e.g. while a profile is under review). Optional steps have no completion
 * rule of their own, so "added" is read from the data each section stores.
 */
const OPTIONAL_CONTENT = {
    '/video-interview': user => !!user?.hasCompletedVideoInterview?.(),
    '/education': user => !!user?.schools?.length,
    '/certification': user => !!user?.certifications?.length,
    '/awards': user => !!user?.awards?.length,
};

export const getOptionalSuggestions = (user) => {
    const steps = user?.getFullProfileStepsForTalent?.() || [];
    const optional = steps
        .filter(step => step?.StepTitle && step?.StepInfo && OPTIONAL_CONTENT[step.path])
        .map(step => ({
            step,
            title: step.StepTitle,
            path: step.path,
            minutes: getStepMinutes(step),
            added: OPTIONAL_CONTENT[step.path](user),
        }));

    return {
        all: optional,
        remaining: optional.filter(s => !s.added),
        addedCount: optional.filter(s => s.added).length,
    };
};

/**
 * Profile-phase "where you are" screens show once per login. The flag lives
 * in sessionStorage and is cleared on login/logout (see Auth.js).
 */
const STATUS_SEEN_PREFIX = 'talentStatusSeen.';

export const hasSeenTalentStatus = (user) => {
    try {
        return !!window.sessionStorage.getItem(STATUS_SEEN_PREFIX + user?.id);
    } catch (e) {
        return true; // storage unavailable: don't keep redirecting
    }
};

export const markTalentStatusSeen = (user) => {
    try {
        window.sessionStorage.setItem(STATUS_SEEN_PREFIX + user?.id, '1');
    } catch (e) {
        // storage unavailable
    }
};

export const clearTalentStatusSeen = () => {
    try {
        Object.keys(window.sessionStorage)
            .filter(key => key.startsWith(STATUS_SEEN_PREFIX))
            .forEach(key => window.sessionStorage.removeItem(key));
    } catch (e) {
        // storage unavailable
    }
};
